import type {
  TransactionalLogEventPayload,
  TransactionalLogResult,
  MetaDim,
} from 'services/transactionalLogs'

const FLOW_ID_PREFIX = 'video_consult_apt'
const TARGET_TYPE = 'call'
const PROVIDER_AGORA = 'agora'
const META_SCREEN = { screen: 'VideoRoom' as const }

const META_DIM_SCREEN: MetaDim = { screen: 'VideoRoom' }

export interface CallLogContext {
  appointmentId: string
  correlationId: string
}

export type CallEventKey =
  | 'join.requested'
  | 'join.succeeded'
  | 'join.failed'
  | 'leave'
  | 'microphone.toggled'
  | 'video.toggled'
  | 'fullscreen.toggled'
  | 'background.changed'
  | 'camera.changed'
  | 'microphone.device.changed'
  | 'token.expired'
  | 'connection.quality.degraded'
  | 'connection.quality.recovered'

export interface CallEventExtra {
  errorCode?: string
  errorMessage?: string
  httpStatus?: number
  muted?: boolean
  enabled?: boolean
  fullscreen?: boolean
  backgroundType?: 'none' | 'blur' | 'image'
  deviceId?: string
  connectionStatus?: string
  previousConnectionStatus?: string
  networkMessage?: string
  txQuality?: number
  rxQuality?: number
  overallQuality?: number
  browserEffectiveType?: string
  browserDownlink?: number
  browserRtt?: number
}

function flowId(appointmentId: string): string {
  return `${FLOW_ID_PREFIX}_${appointmentId}`
}

function trace(ctx: CallLogContext) {
  return {
    correlation_id: ctx.correlationId,
    flow_id: flowId(ctx.appointmentId),
  }
}

function target(ctx: CallLogContext) {
  return {
    type: TARGET_TYPE,
    id: ctx.appointmentId,
    ref: { appointment_id: ctx.appointmentId, provider: PROVIDER_AGORA },
  }
}

function basePayload(
  ctx: CallLogContext,
  result: TransactionalLogResult,
  meta?: Record<string, unknown>,
  action?: Record<string, unknown>,
  meta_dim?: MetaDim
): TransactionalLogEventPayload {
  return {
    trace: trace(ctx),
    target: target(ctx),
    result,
    ...(meta && { meta }),
    ...(action && { action }),
    ...(meta_dim && Object.keys(meta_dim).length > 0 && { meta_dim }),
  }
}

export function buildCallPayload(
  eventKey: CallEventKey,
  ctx: CallLogContext,
  extra?: CallEventExtra
): TransactionalLogEventPayload {
  const success = (): TransactionalLogResult => ({
    status: 'success',
    http_status: 200,
  })

  switch (eventKey) {
    case 'join.requested':
    case 'join.succeeded':
    case 'leave':
      return basePayload(
        ctx,
        success(),
        META_SCREEN,
        undefined,
        META_DIM_SCREEN
      )

    case 'join.failed':
      return basePayload(
        ctx,
        {
          status: 'failure',
          http_status: extra?.httpStatus ?? 500,
          error_code: extra?.errorCode ?? 'CALL_JOIN_FAILED',
          error_message: extra?.errorMessage ?? '',
        },
        META_SCREEN,
        undefined,
        META_DIM_SCREEN
      )

    case 'microphone.toggled':
      return basePayload(
        ctx,
        success(),
        { ...META_SCREEN, muted: extra?.muted },
        { intent: extra?.muted ? 'mute' : 'unmute' },
        { ...META_DIM_SCREEN, muted: extra?.muted ?? false }
      )

    case 'video.toggled':
      return basePayload(
        ctx,
        success(),
        { ...META_SCREEN, video_enabled: extra?.enabled },
        { intent: extra?.enabled ? 'video_on' : 'video_off' },
        { ...META_DIM_SCREEN, video_enabled: extra?.enabled ?? false }
      )

    case 'fullscreen.toggled':
      return basePayload(
        ctx,
        success(),
        { ...META_SCREEN, fullscreen: extra?.fullscreen },
        undefined,
        { ...META_DIM_SCREEN, fullscreen: extra?.fullscreen ?? false }
      )

    case 'background.changed':
      return basePayload(
        ctx,
        success(),
        { ...META_SCREEN, background_type: extra?.backgroundType },
        undefined,
        { ...META_DIM_SCREEN, background_type: extra?.backgroundType ?? 'none' }
      )

    case 'camera.changed':
    case 'microphone.device.changed':
      return basePayload(
        ctx,
        success(),
        { ...META_SCREEN, device_id: extra?.deviceId },
        undefined,
        {
          ...META_DIM_SCREEN,
          ...(extra?.deviceId && { device_id: extra.deviceId }),
        }
      )

    case 'token.expired':
      return basePayload(
        ctx,
        {
          status: 'failure',
          http_status: 401,
          error_code: 'AGORA_TOKEN_EXPIRED',
          error_message: 'token expired',
        },
        META_SCREEN,
        undefined,
        META_DIM_SCREEN
      )

    case 'connection.quality.degraded':
      return basePayload(
        ctx,
        {
          status: 'failure',
          http_status: 503,
          error_code: 'CONNECTION_QUALITY_DEGRADED',
          error_message: extra?.networkMessage ?? 'connection degraded',
        },
        {
          ...META_SCREEN,
          connection_status: extra?.connectionStatus ?? null,
          previous_connection_status: extra?.previousConnectionStatus ?? null,
          network_message: extra?.networkMessage ?? null,
          tx_quality: extra?.txQuality ?? null,
          rx_quality: extra?.rxQuality ?? null,
          overall_quality: extra?.overallQuality ?? null,
          browser_effective_type: extra?.browserEffectiveType ?? null,
          browser_downlink: extra?.browserDownlink ?? null,
          browser_rtt: extra?.browserRtt ?? null,
        },
        undefined,
        {
          ...META_DIM_SCREEN,
          connection_status: extra?.connectionStatus ?? null,
          previous_connection_status: extra?.previousConnectionStatus ?? null,
          network_message: extra?.networkMessage ?? null,
          tx_quality: extra?.txQuality ?? null,
          rx_quality: extra?.rxQuality ?? null,
          overall_quality: extra?.overallQuality ?? null,
          browser_effective_type: extra?.browserEffectiveType ?? null,
          browser_downlink: extra?.browserDownlink ?? null,
          browser_rtt: extra?.browserRtt ?? null,
        }
      )

    case 'connection.quality.recovered':
      return basePayload(
        ctx,
        success(),
        {
          ...META_SCREEN,
          connection_status: extra?.connectionStatus ?? null,
          previous_connection_status: extra?.previousConnectionStatus ?? null,
          network_message: extra?.networkMessage ?? null,
          tx_quality: extra?.txQuality ?? null,
          rx_quality: extra?.rxQuality ?? null,
          overall_quality: extra?.overallQuality ?? null,
          browser_effective_type: extra?.browserEffectiveType ?? null,
          browser_downlink: extra?.browserDownlink ?? null,
          browser_rtt: extra?.browserRtt ?? null,
        },
        undefined,
        {
          ...META_DIM_SCREEN,
          connection_status: extra?.connectionStatus ?? null,
          previous_connection_status: extra?.previousConnectionStatus ?? null,
          network_message: extra?.networkMessage ?? null,
          tx_quality: extra?.txQuality ?? null,
          rx_quality: extra?.rxQuality ?? null,
          overall_quality: extra?.overallQuality ?? null,
          browser_effective_type: extra?.browserEffectiveType ?? null,
          browser_downlink: extra?.browserDownlink ?? null,
          browser_rtt: extra?.browserRtt ?? null,
        }
      )

    default:
      return basePayload(
        ctx,
        success(),
        META_SCREEN,
        undefined,
        META_DIM_SCREEN
      )
  }
}

const EVENT_NAME_PREFIX = 'call.'

export function callEventName(key: CallEventKey): string {
  return `${EVENT_NAME_PREFIX}${key}`
}
